// src/components/agent/Composer.js
//
// Replaces MessageInput (frontend spec §8.8). The text field is NEVER
// disabled (§6.5). While a turn runs the send control renders as Stop
// (§7.6); a submit steers — the session stops the turn and queues the
// message. Drag-and-drop covers the centre column with the same extension
// allowlist as the picker and a 20 MB client-side pre-check per file.
// Exposes restore({text, files}) for the failed-send composer-restore path
// (§7.3). One selection's problems — unsupported, oversized, over the per-message
// cap — are reported together in a single notice, since the page holds one.
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { Box, TextField, IconButton, InputAdornment, Stack, Chip, Tooltip, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditIcon from '@mui/icons-material/Edit';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DuplicateFilesPrompt, { splitLikelyDuplicates } from '../common/DuplicateFilesPrompt';

// v1's accept list — the same allowlist is enforced on drop (the `accept`
// attribute filters only the file dialog).
const ACCEPT_LIST = '.xlsx,.xls,.csv,.json,.docx,.png,.jpg,.jpeg,.gif,.webp,.pdf';
const ALLOWED_EXTENSIONS = ACCEPT_LIST.split(',');
// The backend's request limits (agent_service/channels/uploads.py and web.py).
// Each is checked here so a doomed request is never sent: a file over the
// per-file limit, more uploads than the per-message cap, uploads whose sizes
// add up to more than the combined limit, or a message over the text limit
// (X13-3). Unsent canvas edits travel as one more upload (canvas_edit_file),
// so they take one of the ten; their size is only known once exported, so it
// is the backend's own message that reports a combined total they tip over.
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_FILES = 10;
export const MAX_TOTAL_UPLOAD_BYTES = 40 * 1024 * 1024;
const MAX_MESSAGE_BYTES = 256 * 1024;

const totalBytes = (files) => files.reduce((sum, file) => sum + file.size, 0);

const partitionFiles = (incoming) => {
  const accepted = [];
  const unsupported = [];
  const oversized = [];
  for (const file of incoming) {
    const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      unsupported.push(file.name);
    } else if (file.size > MAX_FILE_BYTES) {
      oversized.push(file.name);
    } else {
      accepted.push(file);
    }
  }
  return { accepted, unsupported, oversized };
};

const Composer = forwardRef(function Composer({
  onSend,
  onStop,
  canStop,
  stopping,
  queued,
  canvasEditInfo, // { filename, count } | null
  contextPct,
  notify,
  dragContainerRef,
}, ref) {
  const [message, setMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  // The drop handler is bound once, so it reads the live selection through these.
  const selectedFilesRef = useRef(selectedFiles);
  selectedFilesRef.current = selectedFiles;
  const canvasEditInfoRef = useRef(canvasEditInfo);
  canvasEditInfoRef.current = canvasEditInfo;
  // Newly added files that look like ones already attached, awaiting Keep / Discard.
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);

  const hasContent = message.trim() !== '' || selectedFiles.length > 0 || !!canvasEditInfo;
  // Canvas edits can appear after the files were attached, so the cap is also
  // checked at send time rather than only as files arrive.
  const uploadCount = selectedFiles.length + (canvasEditInfo ? 1 : 0);
  const overUploadCap = uploadCount > MAX_FILES;
  // Files are held back as they arrive, but a restored draft brings back what
  // it held, so the combined size is checked at send time too.
  const overTotalCap = totalBytes(selectedFiles) > MAX_TOTAL_UPLOAD_BYTES;
  // The text limit is in bytes of UTF-8, which is how the backend measures it.
  const overMessageCap = new TextEncoder().encode(message).length > MAX_MESSAGE_BYTES;
  const overAnyLimit = overUploadCap || overTotalCap || overMessageCap;
  // A submit is never gated by a running turn — the session steers (§7.6).
  // Only an already-queued message holds further sends.
  const canSend = hasContent && !queued && !overAnyLimit;

  // Hand over what the composer shows and clear it, as Send does. Nothing is
  // handed over while a limit is exceeded — Send is disabled then, and a
  // snackbar Retry must not send what Send would refuse; the field already
  // shows why.
  const takeDraft = () => {
    if (overAnyLimit) return null;
    const draft = { text: message.trim(), files: selectedFiles };
    setMessage('');
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    return draft;
  };

  useImperativeHandle(ref, () => ({
    restore: ({ text, files }) => {
      setMessage(text || '');
      setSelectedFiles(files || []);
    },
    // A snackbar Retry sends the draft as it stands now — attachments removed
    // and text edited since the failed send included (M225).
    takeDraft,
  }));
  // Keep showing Stop while the stop drains so the control doesn't flap back
  // to Send before the turn's document commits.
  const showStop = canStop || stopping;

  // Attach as many of `files` as the per-message cap and the combined size
  // limit leave room for; the rest are named in the returned lists so the
  // caller can report them.
  const takeWithinCap = (files) => {
    const taken = selectedFilesRef.current.length + (canvasEditInfoRef.current ? 1 : 0);
    const room = Math.max(0, MAX_FILES - taken);
    const within = [];
    const overCap = [];
    const overTotal = [];
    let bytesSoFar = totalBytes(selectedFilesRef.current);
    // Only an attached file takes a slot: one held back for its size leaves
    // its slot to the next.
    for (const file of files) {
      if (within.length >= room) {
        overCap.push(file.name);
      } else if (bytesSoFar + file.size > MAX_TOTAL_UPLOAD_BYTES) {
        overTotal.push(file.name);
      } else {
        within.push(file);
        bytesSoFar += file.size;
      }
    }
    return { within, overCap, overTotal };
  };

  const reportSkipped = ({ unsupported = [], oversized = [], overCap = [], overTotal = [] }) => {
    const problems = [
      unsupported.length > 0 ? `Skipped unsupported file${unsupported.length > 1 ? 's' : ''}: ${unsupported.join(', ')}.` : null,
      oversized.length > 0 ? `Over the 20 MB limit: ${oversized.join(', ')}.` : null,
      overCap.length > 0 ? `Over the ${MAX_FILES}-upload limit per message${canvasEditInfoRef.current ? ' (canvas edits count as one)' : ''}: ${overCap.join(', ')}.` : null,
      overTotal.length > 0 ? `Over the 40 MB combined limit per message: ${overTotal.join(', ')}.` : null,
    ].filter(Boolean);
    if (problems.length > 0) notify?.(problems.join(' '), { severity: 'warning' });
  };

  const addFiles = (incoming, { fromDrop = false } = {}) => {
    const { accepted, unsupported, oversized } = partitionFiles(incoming);
    const { within, overCap, overTotal } = takeWithinCap(accepted);
    reportSkipped({ unsupported, oversized, overCap, overTotal });
    if (within.length === 0) return;
    // A file that looks like one already attached (same name, size and modified
    // time) is held for Keep / Discard rather than dropped: it is usually the
    // same file picked twice, but can be a different file with the same metadata.
    const { fresh, likely } = splitLikelyDuplicates(selectedFilesRef.current, within);
    if (fresh.length > 0) setSelectedFiles((prev) => [...prev, ...fresh]);
    if (likely.length > 0) setDuplicatePrompt(likely);
  };

  const handleKeepDuplicates = () => {
    // Files may have been attached while the prompt was up, so the caps are re-checked.
    const { within, overCap, overTotal } = takeWithinCap(duplicatePrompt || []);
    setDuplicatePrompt(null);
    reportSkipped({ overCap, overTotal });
    if (within.length > 0) setSelectedFiles((prev) => [...prev, ...within]);
  };

  const handleSend = () => {
    if (!canSend) return;
    const draft = takeDraft();
    if (draft) onSend(draft.text, draft.files);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  // Drag-and-drop target covering the centre column (§8.8).
  useEffect(() => {
    const container = dragContainerRef?.current;
    if (!container) return undefined;

    const hasFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');

    const handleDragEnter = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setDragActive(true);
    };
    const handleDragOver = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
    };
    const handleDragLeave = (event) => {
      if (!hasFiles(event)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setDragActive(false);
    };
    const handleDrop = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current = 0;
      setDragActive(false);
      addFiles(Array.from(event.dataTransfer.files || []), { fromDrop: true });
    };

    container.addEventListener('dragenter', handleDragEnter);
    container.addEventListener('dragover', handleDragOver);
    container.addEventListener('dragleave', handleDragLeave);
    container.addEventListener('drop', handleDrop);
    return () => {
      container.removeEventListener('dragenter', handleDragEnter);
      container.removeEventListener('dragover', handleDragOver);
      container.removeEventListener('dragleave', handleDragLeave);
      container.removeEventListener('drop', handleDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragContainerRef]);

  const dropOverlay = dragActive && dragContainerRef?.current
    ? createPortal(
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(17, 19, 24, 0.85)' : 'rgba(240, 242, 245, 0.85)',
            border: 2,
            borderStyle: 'dashed',
            borderColor: 'primary.main',
            borderRadius: 1,
            pointerEvents: 'none',
            color: 'text.primary',
          }}
        >
          <UploadFileIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="body1">Drop files to attach</Typography>
        </Box>,
        dragContainerRef.current
      )
    : null;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', width: '100%', px: 2, pb: 1.5, pt: 0.5 }}>
      {dropOverlay}
      <DuplicateFilesPrompt
        files={duplicatePrompt}
        onKeep={handleKeepDuplicates}
        onDiscard={() => setDuplicatePrompt(null)}
      />
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple
        accept={ACCEPT_LIST}
        aria-label="File picker"
        onChange={(e) => {
          addFiles(Array.from(e.target.files || []));
          e.target.value = '';
        }}
      />

      {overUploadCap && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5 }}>
          {`Up to ${MAX_FILES} uploads per message, and canvas edits count as one — remove ${uploadCount - MAX_FILES} file${uploadCount - MAX_FILES === 1 ? '' : 's'} to send.`}
        </Typography>
      )}
      {overTotalCap && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5 }}>
          Attached files add up to more than the 40 MB combined limit per message — remove some to send.
        </Typography>
      )}
      {(canvasEditInfo || selectedFiles.length > 0) && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {canvasEditInfo && (
            <Chip
              icon={<EditIcon />}
              label={`${canvasEditInfo.filename} — ${canvasEditInfo.count} unsent edit${canvasEditInfo.count !== 1 ? 's' : ''}`}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
          {selectedFiles.map((file, index) => (
            <Chip
              key={`${file.name}-${index}`}
              label={file.name}
              onDelete={() => setSelectedFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index))}
              size="small"
            />
          ))}
        </Stack>
      )}

      <TextField
        variant="outlined"
        fullWidth
        multiline
        maxRows={8}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Message the Decode Agent..."
        error={overMessageCap}
        helperText={overMessageCap ? 'This message is over the 256 KB limit — shorten it to send.' : undefined}
        InputProps={{
          sx: { backgroundColor: 'background.paper', borderRadius: 3 },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => fileInputRef.current?.click()} aria-label="Attach files">
                <AttachFileIcon />
              </IconButton>
              {showStop ? (
                <Tooltip title={stopping ? 'Stopping at the next step…' : 'Stop at the next step'}>
                  <span>
                    <IconButton onClick={onStop} disabled={stopping} color="primary" aria-label="Stop">
                      <StopCircleIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton onClick={handleSend} disabled={!canSend} color="primary" aria-label="Send">
                  <SendIcon />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />

      {contextPct !== null && contextPct !== undefined && contextPct > 60 && (
        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flexGrow: 1, height: 3, borderRadius: 1, backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }}>
            <Box sx={{ height: '100%', borderRadius: 1, width: `${Math.min(contextPct, 100)}%`, backgroundColor: contextPct > 90 ? 'warning.main' : 'primary.main', transition: 'width 0.3s ease' }} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{contextPct}%</Typography>
        </Box>
      )}
    </Box>
  );
});

export default Composer;
